/**
 * Herramientas del mapa: menú discreto desde el header (panel flotante) o barra flotante legacy
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import L from 'leaflet'
import { useUIStore, useNodeStore, useAuthStore } from '@/stores'
import { hasPermission, canImportExport } from '@/utils/permissions'
import { authService } from '@/services/authService'
import {
  Plus,
  Cable,
  Eye,
  EyeOff,
  ZoomIn,
  ZoomOut,
  Maximize,
  Tag,
  Activity,
  Move,
  FileUp,
  Sparkles,
  ChevronDown,
  Map,
  BoxSelect,
} from 'lucide-react'

export type ToolbarVariant = 'header-menu' | 'floating'

interface ToolbarProps {
  mapInstance: L.Map | null
  showNetworkAssistant?: boolean
  onNetworkAssistant?: () => void
  /** Por defecto: botón en barra superior + panel (no invasivo) */
  variant?: ToolbarVariant
}

export function Toolbar({
  mapInstance,
  showNetworkAssistant,
  onNetworkAssistant,
  variant = 'header-menu',
}: ToolbarProps) {
  const user = useAuthStore((s) => s.user)
  const impOk = user?.role !== 'SUPER_ADMIN' || !!authService.getImpersonateSlug()
  const canWrite =
    impOk && hasPermission(user?.role, 'topology:write', user?.permissions)
  const adv = user?.tenant?.advancedConfig ?? true
  const showImpExp = impOk && canImportExport(user?.role, user?.permissions, adv)

  const mode = useUIStore((state) => state.mode)
  const setMode = useUIStore((state) => state.setMode)
  const showLabels = useUIStore((state) => state.showLabels)
  const toggleLabels = useUIStore((state) => state.toggleLabels)
  const showCables = useUIStore((state) => state.showCables)
  const toggleCables = useUIStore((state) => state.toggleCables)
  const toggleTracePanel = useUIStore((state) => state.toggleTracePanel)
  const toggleImportExportModal = useUIStore((state) => state.toggleImportExportModal)
  const editModeEnabled = useUIStore((state) => state.editModeEnabled)
  const toggleEditMode = useUIStore((state) => state.toggleEditMode)
  const mapSelectMode = useUIStore((state) => state.mapSelectMode)
  const setMapSelectMode = useUIStore((state) => state.setMapSelectMode)
  const nodes = useNodeStore((state) => state.nodes)

  const [menuOpen, setMenuOpen] = useState(false)
  const triggerWrapRef = useRef<HTMLDivElement>(null)
  const [panelRect, setPanelRect] = useState({ top: 0, left: 0, width: 320 })

  const handleZoomIn = () => {
    if (mapInstance) mapInstance.zoomIn()
  }

  const handleZoomOut = () => {
    if (mapInstance) mapInstance.zoomOut()
  }

  const handleFitBounds = () => {
    if (mapInstance && nodes.length > 0) {
      const bounds = L.latLngBounds(nodes.map((node) => [node.lat, node.lng]))
      mapInstance.fitBounds(bounds, { padding: [50, 50] })
    }
  }

  const updatePanelPosition = useCallback(() => {
    const el = triggerWrapRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const width = Math.min(340, window.innerWidth - 16)
    let left = r.right - width
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8))
    const top = r.bottom + 6
    setPanelRect({ top, left, width })
  }, [])

  useLayoutEffect(() => {
    if (!menuOpen || variant !== 'header-menu') return
    updatePanelPosition()
  }, [menuOpen, variant, updatePanelPosition])

  useEffect(() => {
    if (!menuOpen || variant !== 'header-menu') return
    const onScroll = () => updatePanelPosition()
    const onResize = () => updatePanelPosition()
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [menuOpen, variant, updatePanelPosition])

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  const mapToolsActive =
    mode !== 'view' || editModeEnabled || mapSelectMode === 'rect' || !showLabels || !showCables

  const toolGrid = (layout: 'wrap' | 'row') => (
    <div
      className={
        layout === 'wrap'
          ? 'flex flex-wrap gap-1 justify-start'
          : 'flex flex-nowrap items-center gap-1'
      }
    >
      <ToolButton
        icon={<Eye className="w-5 h-5" />}
        label="Ver"
        active={mode === 'view'}
        onClick={() => setMode('view')}
        compact
      />

      {canWrite && (
        <>
          <ToolButton
            icon={<Plus className="w-5 h-5" />}
            label="Agregar Nodo"
            active={mode === 'addNode'}
            onClick={() => setMode('addNode')}
            compact
          />
          <ToolButton
            icon={<Cable className="w-5 h-5" />}
            label="Agregar Cable"
            active={mode === 'addCable'}
            onClick={() => setMode('addCable')}
            compact
          />
          <ToolButton
            icon={<Move className="w-5 h-5" />}
            label={editModeEnabled ? 'Desactivar Mover Nodos' : 'Mover Nodos'}
            active={editModeEnabled}
            onClick={toggleEditMode}
            compact
          />
          <ToolButton
            icon={<BoxSelect className="w-5 h-5" />}
            label={mapSelectMode === 'rect' ? 'Salir selección área' : 'Selección por área'}
            active={mapSelectMode === 'rect'}
            onClick={() => {
              if (mapSelectMode === 'rect') {
                setMapSelectMode('navigate')
              } else {
                setMapSelectMode('rect')
                setMode('view')
              }
            }}
            compact
          />
        </>
      )}

      <div className="w-px h-7 bg-gray-200 dark:bg-dark-600 mx-0.5 shrink-0 self-center" />

      <ToolButton
        icon={<Tag className="w-5 h-5" />}
        label={showLabels ? 'Ocultar Etiquetas' : 'Mostrar Etiquetas'}
        active={showLabels}
        onClick={toggleLabels}
        compact
      />
      <ToolButton
        icon={showCables ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
        label={showCables ? 'Ocultar Cables' : 'Mostrar Cables'}
        active={showCables}
        onClick={toggleCables}
        compact
      />

      <div className="w-px h-7 bg-gray-200 dark:bg-dark-600 mx-0.5 shrink-0 self-center" />

      <ToolButton
        icon={<ZoomIn className="w-5 h-5" />}
        label="Acercar"
        onClick={handleZoomIn}
        disabled={!mapInstance}
        compact
      />
      <ToolButton
        icon={<ZoomOut className="w-5 h-5" />}
        label="Alejar"
        onClick={handleZoomOut}
        disabled={!mapInstance}
        compact
      />
      <ToolButton
        icon={<Maximize className="w-5 h-5" />}
        label="Ajustar Vista"
        onClick={handleFitBounds}
        disabled={!mapInstance || nodes.length === 0}
        compact
      />

      <div className="w-px h-7 bg-gray-200 dark:bg-dark-600 mx-0.5 shrink-0 self-center" />

      <ToolButton
        icon={<Activity className="w-5 h-5" />}
        label="Trazabilidad de Fibra"
        onClick={() => {
          toggleTracePanel()
          setMenuOpen(false)
        }}
        compact
      />

      {impOk && showNetworkAssistant && onNetworkAssistant && (
        <ToolButton
          icon={<Sparkles className="w-5 h-5 text-violet-500" />}
          label="Asistente de red (IA)"
          onClick={() => {
            onNetworkAssistant()
            setMenuOpen(false)
          }}
          compact
        />
      )}

      {showImpExp && (
        <ToolButton
          icon={<FileUp className="w-5 h-5" />}
          label="Importar / Exportar (KML, CSV, imagen)"
          onClick={() => {
            toggleImportExportModal()
            setMenuOpen(false)
          }}
          compact
        />
      )}
    </div>
  )

  if (variant === 'header-menu') {
    const portal = menuOpen
      ? createPortal(
        <>
          <button
            type="button"
            className="fixed inset-0 z-[9998] cursor-default bg-black/15 dark:bg-black/35"
            aria-label="Cerrar menú de herramientas"
            onClick={() => setMenuOpen(false)}
          />
          <div
            role="dialog"
            aria-label="Herramientas del mapa"
            className="fixed z-[9999] max-h-[min(72vh,520px)] overflow-y-auto rounded-xl border border-gray-200 bg-white p-3 shadow-2xl dark:border-dark-600 dark:bg-dark-800"
            style={{
              top: panelRect.top,
              left: panelRect.left,
              width: panelRect.width,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
              Herramientas del mapa
            </p>
            {toolGrid('wrap')}
            <p className="mt-3 border-t border-gray-100 pt-2 text-[10px] text-gray-400 dark:border-dark-600 dark:text-gray-500">
              Cierra con Escape o pulsando fuera. El mapa queda siempre visible.
            </p>
          </div>
        </>,
        document.body
      )
      : null

    return (
      <div ref={triggerWrapRef} className="relative shrink-0">
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          aria-expanded={menuOpen}
          aria-haspopup="dialog"
          className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1.5 text-sm transition-colors ${
            menuOpen || mapToolsActive
              ? 'border-primary-400 bg-primary-50 text-primary-800 dark:border-primary-600 dark:bg-primary-900/30 dark:text-primary-200'
              : 'border-gray-200 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700'
          }`}
          title="Herramientas del mapa (vista, edición, zoom, trazabilidad)"
        >
          <Map className="h-4 w-4 shrink-0" />
          <span className="hidden sm:inline">Mapa</span>
          <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
        </button>
        {portal}
      </div>
    )
  }

  /* Legacy: barra flotante centrada sobre el mapa */
  return (
    <div className="absolute top-20 left-1/2 z-[1000] w-[calc(100vw-0.75rem)] max-w-[min(100vw-0.75rem,1200px)] -translate-x-1/2 px-1">
      <p className="sr-only">
        Barra de herramientas del mapa: modos de edición, zoom, trazabilidad e importar o exportar.
      </p>
      <div className="max-w-full overflow-x-auto overflow-y-visible pb-px">
        <div className="mx-auto w-max rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-dark-700 dark:bg-dark-800">
          {toolGrid('row')}
        </div>
      </div>
      <p className="mt-0.5 hidden text-center text-[10px] text-gray-500 opacity-80 sm:block dark:text-gray-400">
        Importar / Exportar · Trazabilidad · Mover nodos
      </p>
    </div>
  )
}

interface ToolButtonProps {
  icon: React.ReactNode
  label: string
  active?: boolean
  disabled?: boolean
  onClick: () => void
  compact?: boolean
}

function ToolButton({
  icon,
  label,
  active = false,
  disabled = false,
  onClick,
  compact = false,
}: ToolButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`
        ${compact ? 'p-1.5' : 'p-2'} relative shrink-0 rounded transition-colors group
        ${
          disabled
            ? 'cursor-not-allowed opacity-40'
            : active
              ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
              : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-dark-700'
        }
      `}
    >
      <span
        className={`inline-flex items-center justify-center ${compact ? '[&_svg]:h-4 [&_svg]:w-4' : '[&_svg]:h-5 [&_svg]:w-5'}`}
      >
        {icon}
      </span>
      <span className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 dark:bg-dark-700">
        {label}
      </span>
    </button>
  )
}
